import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { useTranslation, Trans } from "react-i18next";
import { fmtDate } from "@/lib/locale";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useState } from "react";
import {
  Plus, Search, Car, Clock, Building2, Filter, X, Edit, Trash2,
  ShoppingBag, Star, MapPin, Settings, CheckCircle2, ChevronDown, ChevronUp,
} from "lucide-react";
import type { MarketplaceListingWithDetails, DealerReviewsResult, MarketplaceReviewWithDetails } from "@shared/schema";

const BODY_STYLES = ["Sedan", "SUV", "Truck", "Coupe", "Convertible", "Wagon", "Van", "Hatchback", "Minivan"];

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  sold: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  draft: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
};

function formatPrice(price: string | null | undefined) {
  if (!price) return "—";
  const n = parseFloat(price);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function formatMileage(m: number | null | undefined) {
  if (m == null) return "—";
  return m.toLocaleString() + " mi";
}

function StarBadge({ avg, count }: { avg: number; count: number }) {
  return (
    <span
      data-testid="badge-star-rating"
      className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400"
    >
      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
      {avg.toFixed(1)}
      <span className="text-muted-foreground">· {count}</span>
    </span>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          data-testid={`star-${star}`}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          className="p-0.5"
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              star <= (hovered || value)
                ? "fill-amber-400 text-amber-400"
                : "text-muted-foreground/30"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: MarketplaceReviewWithDetails }) {
  const displayName = review.reviewerDealershipName || review.reviewerUsername;
  const date = review.createdAt
    ? fmtDate(review.createdAt as unknown as string, { year: "numeric", month: "short", day: "numeric" })
    : "—";
  return (
    <div className="p-4 rounded-lg border border-border bg-card space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm" data-testid={`review-reviewer-${review.id}`}>{displayName}</span>
        <span className="text-xs text-muted-foreground shrink-0" data-testid={`review-date-${review.id}`}>{date}</span>
      </div>
      <div className="flex items-center gap-0.5" data-testid={`review-stars-${review.id}`}>
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={`w-3.5 h-3.5 ${s <= review.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`}
          />
        ))}
      </div>
      {review.reviewText && (
        <p className="text-sm text-muted-foreground leading-relaxed" data-testid={`review-text-${review.id}`}>
          {review.reviewText}
        </p>
      )}
    </div>
  );
}

function ListingCard({ listing }: { listing: MarketplaceListingWithDetails }) {
  const { t } = useTranslation(["marketplace", "common"]);
  const [, navigate] = useLocation();
  const heroPhoto = listing.photos[0];

  return (
    <button
      type="button"
      data-testid={`listing-card-${listing.id}`}
      onClick={() => navigate(`/marketplace/${listing.id}`)}
      className="group text-left rounded-xl border border-border bg-card hover:border-violet-500/40 hover:shadow-md transition-all overflow-hidden"
    >
      <div className="aspect-video bg-muted relative overflow-hidden">
        {heroPhoto ? (
          <img
            src={heroPhoto.photoUrl}
            alt={`${listing.year} ${listing.make} ${listing.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const t = e.currentTarget;
              t.style.display = "none";
              const ph = t.nextElementSibling as HTMLElement | null;
              if (ph) ph.style.display = "flex";
            }}
          />
        ) : null}
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ display: heroPhoto ? "none" : "flex" }}
        >
          <Car className="w-12 h-12 text-muted-foreground/30" />
        </div>
        {listing.photos.length > 1 && (
          <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
            {t("marketplace:units.photosMore", { count: listing.photos.length - 1 })}
          </span>
        )}
        {listing.status === "pending" && (
          <span
            data-testid={`badge-pending-${listing.id}`}
            className="absolute top-2 left-2 bg-amber-500 text-white text-xs font-semibold px-2 py-0.5 rounded"
          >
            {t("marketplace:status.pendingSale")}
          </span>
        )}
        {listing.status === "sold" && (
          <>
            <div className="absolute inset-0 bg-black/40 pointer-events-none" />
            <span
              data-testid={`badge-sold-${listing.id}`}
              className="absolute top-2 left-2 bg-gray-800 text-white text-xs font-semibold px-2 py-0.5 rounded"
            >
              {t("marketplace:status.sold")}
            </span>
          </>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-sm leading-tight truncate">
            {listing.year} {listing.make} {listing.model}
            {listing.trim ? ` ${listing.trim}` : ""}
          </h3>
          <span className="font-bold text-sm text-violet-600 dark:text-violet-400 shrink-0">
            {formatPrice(listing.askingPrice)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {listing.mileage != null && (
            <span data-testid={`text-mileage-${listing.id}`} className="flex items-center gap-1">
              <Car className="w-3 h-3" />{formatMileage(listing.mileage)}
            </span>
          )}
          {listing.bodyStyle && <span>{listing.bodyStyle}</span>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-y-1 text-xs text-muted-foreground pt-1 border-t border-border">
          <span className="flex items-center gap-2 min-w-0">
            <span className="flex items-center gap-1 min-w-0">
              <Building2 className="w-3 h-3 shrink-0" />
              <span className="truncate">{listing.sellerDealershipName || listing.sellerUsername}</span>
            </span>
            {listing.sellerAvgRating != null && listing.sellerReviewCount > 0 && (
              <StarBadge avg={listing.sellerAvgRating} count={listing.sellerReviewCount} />
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" />
            {listing.daysListed === 0 ? t("marketplace:units.today") : t("marketplace:units.daysShort", { count: listing.daysListed })}
          </span>
        </div>
        {(listing.sellerCity || listing.sellerState) && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground/70 mt-1">
            <MapPin className="w-3 h-3 shrink-0" />
            <span data-testid={`text-seller-location-${listing.id}`} className="truncate">
              {[listing.sellerCity, listing.sellerState].filter(Boolean).join(", ")}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

const RADIUS_OPTIONS = ["5", "10", "15", "25", "50", "100"];

// Parse filter state from URL search params
function parseFilters(search: string) {
  const p = new URLSearchParams(search);
  return {
    search: p.get("q") ?? "",
    vin: p.get("vin") ?? "",
    make: p.get("make") ?? "all",
    model: p.get("model") ?? "all",
    bodyStyle: p.get("bodyStyle") ?? "all",
    yearMin: p.get("yearMin") ?? "",
    yearMax: p.get("yearMax") ?? "",
    priceMin: p.get("priceMin") ?? "",
    priceMax: p.get("priceMax") ?? "",
    nearZip: p.get("nearZip") ?? "",
    radiusMiles: p.get("radiusMiles") ?? "25",
    sortBy: p.get("sortBy") ?? "default",
    showFilters: p.get("filters") === "1",
  };
}

function formatPriceInput(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-US");
}

function BrowseTab() {
  const { t } = useTranslation(["marketplace", "common"]);
  const [location, navigate] = useLocation();
  const rawSearch = typeof window !== "undefined" ? window.location.search : "";
  const filters = useMemo(() => parseFilters(rawSearch), [rawSearch]);

  const emptyDraft = () => ({
    search: filters.search,
    vin: filters.vin,
    make: filters.make,
    model: filters.model,
    bodyStyle: filters.bodyStyle,
    yearMin: filters.yearMin,
    yearMax: filters.yearMax,
    priceMin: formatPriceInput(filters.priceMin),
    priceMax: formatPriceInput(filters.priceMax),
    nearZip: filters.nearZip,
    radiusMiles: filters.radiusMiles,
    sortBy: filters.sortBy,
  });

  const [draftFilters, setDraftFilters] = useState(emptyDraft);

  function setDraft(key: string, value: string) {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handlePriceChange(key: "priceMin" | "priceMax", raw: string) {
    setDraftFilters((prev) => ({ ...prev, [key]: formatPriceInput(raw) }));
  }

  function setFilter(key: string, value: string) {
    const p = new URLSearchParams(window.location.search);
    if (value && value !== "all" && value !== "") {
      p.set(key, value);
    } else {
      p.delete(key);
    }
    navigate(`/marketplace?${p.toString()}`, { replace: true });
  }

  function toggleFilters() {
    const p = new URLSearchParams(window.location.search);
    if (filters.showFilters) {
      p.delete("filters");
      navigate(`/marketplace?${p.toString()}`, { replace: true });
    } else {
      setDraftFilters(emptyDraft());
      p.set("filters", "1");
      navigate(`/marketplace?${p.toString()}`, { replace: true });
    }
  }

  function applyFilters() {
    const p = new URLSearchParams(window.location.search);
    const set = (urlKey: string, val: string, emptyVal = "") => {
      if (val && val !== "all" && val !== emptyVal) p.set(urlKey, val);
      else p.delete(urlKey);
    };
    set("make", draftFilters.make);
    set("model", draftFilters.model);
    set("bodyStyle", draftFilters.bodyStyle);
    set("yearMin", draftFilters.yearMin);
    set("yearMax", draftFilters.yearMax);
    set("priceMin", draftFilters.priceMin.replace(/,/g, ""));
    set("priceMax", draftFilters.priceMax.replace(/,/g, ""));
    set("vin", draftFilters.vin);
    set("nearZip", draftFilters.nearZip);
    set("radiusMiles", draftFilters.radiusMiles);
    set("sortBy", draftFilters.sortBy === "default" ? "" : draftFilters.sortBy);
    p.delete("filters");
    navigate(`/marketplace?${p.toString()}`, { replace: true });
  }

  function backFilters() {
    const p = new URLSearchParams(window.location.search);
    p.delete("filters");
    navigate(`/marketplace?${p.toString()}`, { replace: true });
  }

  function clearFilters() {
    navigate("/marketplace", { replace: true });
  }

  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filters.make && filters.make !== "all") p.make = filters.make;
    if (filters.model && filters.model !== "all") p.model = filters.model;
    if (filters.bodyStyle && filters.bodyStyle !== "all") p.bodyStyle = filters.bodyStyle;
    if (filters.yearMin) p.yearMin = filters.yearMin;
    if (filters.yearMax) p.yearMax = filters.yearMax;
    if (filters.priceMin) p.priceMin = filters.priceMin;
    if (filters.priceMax) p.priceMax = filters.priceMax;
    if (filters.vin.trim()) p.vin = filters.vin.trim();
    if (filters.nearZip.trim().length === 5) {
      p.nearZip = filters.nearZip.trim();
      p.radiusMiles = filters.radiusMiles;
    }
    return p;
  }, [filters]);

  const { data: marketplaceData, isLoading, isFetching } = useQuery<{ listings: MarketplaceListingWithDetails[]; zipResolved: boolean | null }>({
    queryKey: ["/api/marketplace", queryParams],
    queryFn: async () => {
      const params = new URLSearchParams(queryParams);
      const res = await apiRequest("GET", `/api/marketplace?${params.toString()}`);
      return res.json();
    },
  });
  const listings = marketplaceData?.listings ?? [];

  const filteredListings = useMemo(() => {
    if (!filters.search) return listings;
    const q = filters.search.toLowerCase();
    return listings.filter((l) =>
      [l.make, l.model, l.trim, l.vin, l.color, l.bodyStyle]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q))
    );
  }, [listings, filters.search]);

  const zipNotFound = marketplaceData?.zipResolved === false && !isFetching;

  const sortedListings = useMemo(() => {
    const price = (l: MarketplaceListingWithDetails) => parseFloat(l.askingPrice ?? "0") || 0;
    if (filters.sortBy === "price_asc") {
      return [...filteredListings].sort((a, b) => price(a) - price(b));
    }
    if (filters.sortBy === "price_desc") {
      return [...filteredListings].sort((a, b) => price(b) - price(a));
    }
    return filteredListings;
  }, [filteredListings, filters.sortBy]);


  const hasFilters =
    filters.make !== "all" ||
    filters.model !== "all" ||
    filters.bodyStyle !== "all" ||
    !!filters.yearMin ||
    !!filters.yearMax ||
    !!filters.priceMin ||
    !!filters.priceMax ||
    !!filters.vin.trim() ||
    !!filters.nearZip.trim() ||
    filters.sortBy !== "default";

  return (
    <div className="space-y-4">
      {/* Search + Filters bar */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="input-search-marketplace"
            placeholder={t("marketplace:browse.searchPlaceholder")}
            value={filters.search}
            onChange={(e) => setFilter("q", e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="button-toggle-filters"
          onClick={toggleFilters}
          className={filters.showFilters ? "border-violet-500 text-violet-600" : ""}
        >
          <Filter className="w-4 h-4 mr-1.5" />
          {t("marketplace:browse.filters")}
          {hasFilters && <span className="ml-1 w-2 h-2 rounded-full bg-violet-500 inline-block" />}
        </Button>
        {hasFilters && (
          <Button variant="ghost" size="sm" data-testid="button-clear-filters" onClick={clearFilters}>
            <X className="w-3.5 h-3.5 mr-1" />{t("marketplace:browse.clear")}
          </Button>
        )}
      </div>

      {/* Filter panel */}
      {filters.showFilters && (
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-11 gap-3">
              {/* Sort by */}
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.sortBy")}</Label>
                <Select value={draftFilters.sortBy} onValueChange={(v) => setDraft("sortBy", v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid="select-sort-by">
                    <SelectValue placeholder={t("marketplace:browse.sortDefault")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t("marketplace:browse.sortDefault")}</SelectItem>
                    <SelectItem value="price_asc">{t("marketplace:browse.sortPriceAsc")}</SelectItem>
                    <SelectItem value="price_desc">{t("marketplace:browse.sortPriceDesc")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.make")}</Label>
                <Input
                  data-testid="input-filter-make"
                  placeholder={t("marketplace:browse.anyMake")}
                  value={draftFilters.make === "all" ? "" : draftFilters.make}
                  onChange={(e) => setDraft("make", e.target.value || "all")}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.model")}</Label>
                <Input
                  data-testid="input-filter-model"
                  placeholder={t("marketplace:browse.anyModel")}
                  value={draftFilters.model === "all" ? "" : draftFilters.model}
                  onChange={(e) => setDraft("model", e.target.value || "all")}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.bodyStyle")}</Label>
                <Select value={draftFilters.bodyStyle} onValueChange={(v) => setDraft("bodyStyle", v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid="select-body-style">
                    <SelectValue placeholder={t("marketplace:browse.any")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("marketplace:browse.any")}</SelectItem>
                    {BODY_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.yearMin")}</Label>
                <Input data-testid="input-year-min" type="number" placeholder={t("marketplace:browse.yearMinPlaceholder")} value={draftFilters.yearMin} onChange={(e) => setDraft("yearMin", e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.yearMax")}</Label>
                <Input data-testid="input-year-max" type="number" placeholder={t("marketplace:browse.yearMaxPlaceholder")} value={draftFilters.yearMax} onChange={(e) => setDraft("yearMax", e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.priceMin")}</Label>
                <Input data-testid="input-price-min" type="text" inputMode="numeric" placeholder={t("marketplace:browse.priceMinPlaceholder")} value={draftFilters.priceMin} onChange={(e) => handlePriceChange("priceMin", e.target.value)} className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.priceMax")}</Label>
                <Input data-testid="input-price-max" type="text" inputMode="numeric" placeholder={t("marketplace:browse.priceMaxPlaceholder")} value={draftFilters.priceMax} onChange={(e) => handlePriceChange("priceMax", e.target.value)} className="h-8 text-sm" />
              </div>
              {/* ZIP proximity */}
              <div className="space-y-1 col-span-2 sm:col-span-1 xl:col-span-2">
                <Label className="text-xs flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{t("marketplace:browse.nearZip")}
                </Label>
                <div className="flex gap-1">
                  <Input
                    data-testid="input-filter-near-zip"
                    placeholder={t("marketplace:browse.zipCode")}
                    value={draftFilters.nearZip}
                    onChange={(e) => setDraft("nearZip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                    className="h-8 text-sm w-24 shrink-0"
                    maxLength={5}
                  />
                  <Select value={draftFilters.radiusMiles} onValueChange={(v) => setDraft("radiusMiles", v)}>
                    <SelectTrigger className="h-8 text-sm flex-1 min-w-0" data-testid="select-radius">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RADIUS_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{t("marketplace:radius.within", { count: Number(o) })}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {draftFilters.nearZip.length > 0 && draftFilters.nearZip.length < 5 && (
                  <p className="text-xs text-muted-foreground">{t("marketplace:browse.enterZip")}</p>
                )}
              </div>
              {/* VIN — last */}
              <div className="space-y-1">
                <Label className="text-xs">{t("marketplace:browse.vin")}</Label>
                <Input
                  data-testid="input-filter-vin"
                  placeholder={t("marketplace:browse.vinPlaceholder")}
                  value={draftFilters.vin}
                  onChange={(e) => setDraft("vin", e.target.value.toUpperCase())}
                  className="h-8 text-sm font-mono"
                  maxLength={17}
                />
              </div>
            </div>
            {/* Apply / Back row */}
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                data-testid="button-filter-back"
                onClick={backFilters}
              >
                {t("marketplace:browse.back")}
              </Button>
              <Button
                size="sm"
                data-testid="button-filter-apply"
                onClick={applyFilters}
              >
                {t("marketplace:browse.apply")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ZIP not found hint */}
      {zipNotFound && (
        <p className="text-sm text-muted-foreground text-center py-2">
          <Trans i18nKey="marketplace:browse.zipNotFound" values={{ zip: filters.nearZip }} components={{ 1: <strong /> }} />
        </p>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] rounded-xl" />
          ))}
        </div>
      ) : sortedListings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground font-medium">{t("marketplace:browse.noListingsFound")}</p>
          <p className="text-muted-foreground/60 text-sm mt-1">
            {hasFilters || filters.search ? t("marketplace:browse.adjustFilters") : t("marketplace:browse.beFirst")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedListings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      )}
    </div>
  );
}

function MyReputationSection({
  userId,
}: {
  userId: string;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { data, isLoading } = useQuery<DealerReviewsResult>({
    queryKey: ["/api/marketplace/dealers", userId, "reviews"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/dealers/${userId}/reviews`);
      return res.json();
    },
    enabled: !!userId,
  });

  return (
    <div className="space-y-4" data-testid="section-my-reputation">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
        <Star className="w-4 h-4 text-amber-400" />
        {t("marketplace:reputation.title")}
      </h3>

      {isLoading ? (
        <Skeleton className="h-24 rounded-xl" />
      ) : (
        <>
          {/* Aggregate stats */}
          <div className="p-4 rounded-xl border border-border bg-card flex items-center gap-6">
            {data && data.avgRating != null ? (
              <>
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-500" data-testid="text-my-avg-rating">
                    {data.avgRating.toFixed(1)}
                  </p>
                  <div className="flex items-center gap-0.5 mt-1 justify-center">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-4 h-4 ${s <= Math.round(data.avgRating!) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="border-l border-border pl-6">
                  <p className="text-2xl font-bold" data-testid="text-my-review-count">{data.totalCount}</p>
                  <p className="text-xs text-muted-foreground">{t("marketplace:reputation.reviewsReceived", { count: data.totalCount })}</p>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3 text-muted-foreground">
                <Star className="w-8 h-8 text-muted-foreground/20" />
                <div>
                  <p className="text-sm font-medium">{t("marketplace:reputation.noReviews")}</p>
                  <p className="text-xs">{t("marketplace:reputation.noReviewsHint")}</p>
                </div>
              </div>
            )}
          </div>

          {/* Review list */}
          {data && data.reviews.length > 0 && (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1" data-testid="list-my-reviews">
              {data.reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ReviewDialog({
  listing,
  open,
  onClose,
}: {
  listing: MarketplaceListingWithDetails;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");

  const submitReview = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/marketplace/${listing.id}/review`, {
        rating,
        reviewText: reviewText.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/marketplace/my"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace/dealers", listing.sellerUserId, "reviews"] });
      toast({ title: t("marketplace:toast.reviewSubmitted"), description: t("marketplace:toast.reviewSubmittedDesc") });
      onClose();
    },
    onError: async (err: unknown) => {
      let msg = t("marketplace:toast.couldNotSubmitReview");
      if (err instanceof Response) {
        try {
          const data = await err.json();
          msg = data.message || msg;
        } catch {}
      } else if (err instanceof Error) {
        msg = err.message;
      }
      toast({ title: t("marketplace:toast.reviewNotSubmitted"), description: msg, variant: "destructive" });
    },
  });

  function handleClose() {
    setRating(0);
    setReviewText("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="dialog-review">
        <DialogHeader>
          <DialogTitle>{t("marketplace:reviewDialog.title")}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="marketplace:reviewDialog.description"
              values={{ vehicle: `${listing.year} ${listing.make} ${listing.model}` }}
              components={{ 1: <strong /> }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("marketplace:reviewDialog.starRating")}</Label>
            <StarPicker value={rating} onChange={setRating} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="review-text" className="text-sm font-medium">
              {t("marketplace:reviewDialog.reviewLabel")} <span className="text-muted-foreground font-normal">{t("marketplace:reviewDialog.optional")}</span>
            </Label>
            <Textarea
              id="review-text"
              data-testid="input-review-text"
              placeholder={t("marketplace:reviewDialog.placeholder")}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              maxLength={2000}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="button-skip-review">
            {t("marketplace:reviewDialog.skip")}
          </Button>
          <Button
            data-testid="button-submit-review"
            disabled={rating === 0 || submitReview.isPending}
            onClick={() => submitReview.mutate()}
          >
            {submitReview.isPending ? t("marketplace:reviewDialog.submitting") : t("marketplace:reviewDialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SoldChannelDialog({
  listing,
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  listing: MarketplaceListingWithDetails;
  open: boolean;
  onClose: () => void;
  onConfirm: (soldChannel: string, buyerUserId: string | null) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation(["marketplace", "common"]);
  const [channel, setChannel] = useState<"marketplace" | "other">("other");
  const [buyerUserId, setBuyerUserId] = useState<string>("__skip__");

  const { data: buyers = [] } = useQuery<{ conversationId: string; buyerUserId: string; buyerName: string }[]>({
    queryKey: ["/api/marketplace", listing.id, "conversations"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/marketplace/${listing.id}/conversations`);
      return res.json();
    },
    enabled: open,
  });

  const hasBuyers = buyers.length > 0;

  function handleConfirm() {
    const resolvedBuyer = channel === "marketplace" && buyerUserId !== "__skip__" ? buyerUserId : null;
    onConfirm(channel, resolvedBuyer);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("marketplace:soldChannel.title")}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="marketplace:soldChannel.description"
              values={{ vehicle: `${listing.year} ${listing.make} ${listing.model}` }}
              components={{ 1: <strong /> }}
            />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RadioGroup
            value={channel}
            onValueChange={(v) => setChannel(v as "marketplace" | "other")}
            className="gap-3"
            data-testid="radio-sold-channel"
          >
            <label
              htmlFor="channel-marketplace"
              className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                channel === "marketplace"
                  ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <RadioGroupItem value="marketplace" id="channel-marketplace" className="mt-0.5" />
              <div className="min-w-0">
                <p className="font-medium text-sm leading-tight">{t("marketplace:soldChannel.viaMarketplace")}</p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  {t("marketplace:soldChannel.viaMarketplaceHint")}
                </p>
              </div>
            </label>

            <label
              htmlFor="channel-other"
              className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                channel === "other"
                  ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30"
                  : "border-border hover:bg-muted/50"
              }`}
            >
              <RadioGroupItem value="other" id="channel-other" className="mt-0.5" />
              <div className="min-w-0">
                <p className="font-medium text-sm leading-tight">{t("marketplace:soldChannel.elsewhere")}</p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  {t("marketplace:soldChannel.elsewhereHint")}
                </p>
              </div>
            </label>
          </RadioGroup>

          {channel === "marketplace" && hasBuyers && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("marketplace:soldChannel.whoBought")} <span className="text-muted-foreground font-normal">{t("marketplace:reviewDialog.optional")}</span></Label>
              <Select value={buyerUserId} onValueChange={setBuyerUserId}>
                <SelectTrigger data-testid="select-buyer-user-id">
                  <SelectValue placeholder={t("marketplace:soldChannel.selectBuyer")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__skip__">{t("marketplace:soldChannel.skipBuyer")}</SelectItem>
                  {buyers.map((b) => (
                    <SelectItem key={b.buyerUserId} value={b.buyerUserId}>
                      {b.buyerName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="button-cancel-sold-channel">{t("marketplace:soldChannel.cancel")}</Button>
          <Button
            data-testid="button-confirm-sold-channel"
            disabled={isPending}
            onClick={handleConfirm}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {isPending ? t("marketplace:soldChannel.markingSold") : t("marketplace:soldChannel.markSold")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyListingsTab() {
  const { t } = useTranslation(["marketplace", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [deleteTarget, setDeleteTarget] = useState<MarketplaceListingWithDetails | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ listing: MarketplaceListingWithDetails; newStatus: string } | null>(null);
  const [soldChannelTarget, setSoldChannelTarget] = useState<MarketplaceListingWithDetails | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const COLLAPSED_LIMIT = 5;

  const { data: listings = [], isLoading } = useQuery<MarketplaceListingWithDetails[]>({
    queryKey: ["/api/marketplace/my"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/my");
      return res.json();
    },
  });

  const { data: me } = useQuery<{ id: string }>({ queryKey: ["/api/auth/me"], retry: false });

  const updateStatus = useMutation({
    mutationFn: ({ id, status, soldChannel, buyerUserId }: { id: string; status: string; soldChannel?: string | null; buyerUserId?: string | null }) =>
      apiRequest("PATCH", `/api/marketplace/${id}`, { status, soldChannel: soldChannel ?? undefined, buyerUserId: buyerUserId ?? undefined }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/marketplace/my"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace"] });
      toast({ title: t("marketplace:toast.listingUpdated") });
      setSoldChannelTarget(null);
      setStatusTarget(null);
    },
    onError: () => toast({ title: t("marketplace:toast.updateFailed"), variant: "destructive" }),
  });

  const deleteListing = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/marketplace/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/marketplace/my"] });
      qc.invalidateQueries({ queryKey: ["/api/marketplace"] });
      toast({ title: t("marketplace:toast.listingDeleted") });
      setDeleteTarget(null);
    },
    onError: () => toast({ title: t("marketplace:toast.deleteFailed"), variant: "destructive" }),
  });

  const byStatus = useMemo(() => {
    const groups: Record<string, MarketplaceListingWithDetails[]> = { active: [], pending: [], sold: [], draft: [] };
    for (const l of listings) {
      (groups[l.status] ??= []).push(l);
    }
    return groups;
  }, [listings]);

  if (isLoading) {
    return <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>;
  }

  const statusOrder = ["active", "pending", "draft", "sold"];

  return (
    <div className="space-y-6">
      {listings.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground font-medium">{t("marketplace:myListings.noListings")}</p>
          <p className="text-muted-foreground/60 text-sm mt-1 mb-4">{t("marketplace:myListings.getStarted")}</p>
          <Button data-testid="button-new-listing-empty" onClick={() => navigate("/marketplace/new")} size="sm">
            <Plus className="w-4 h-4 mr-1.5" />{t("marketplace:myListings.listVehicle")}
          </Button>
        </div>
      )}
      {statusOrder.filter((s) => byStatus[s]?.length > 0).map((status) => {
        const allInGroup = byStatus[status];
        const collapsible = (status === "active" || status === "sold") && allInGroup.length > COLLAPSED_LIMIT;
        const isExpanded = !!expandedGroups[status];
        const visibleListings = collapsible && !isExpanded ? allInGroup.slice(0, COLLAPSED_LIMIT) : allInGroup;
        const hiddenCount = allInGroup.length - COLLAPSED_LIMIT;
        return (
        <div key={status}>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
            <span>{t(`marketplace:status.${status}`)}</span>
            <Badge variant="secondary" className="text-xs">{allInGroup.length}</Badge>
          </h3>
          <div className="space-y-2">
            {visibleListings.map((listing) => {
              const heroPhoto = listing.photos[0];
              return (
                <div
                  key={listing.id}
                  data-testid={`my-listing-row-${listing.id}`}
                  className="flex items-center gap-4 p-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors"
                >
                  <div className="w-16 h-12 rounded bg-muted shrink-0 overflow-hidden relative">
                    {heroPhoto ? (
                      <img
                        src={heroPhoto.photoUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const t = e.currentTarget;
                          t.style.display = "none";
                          const ph = t.nextElementSibling as HTMLElement | null;
                          if (ph) ph.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ display: heroPhoto ? "none" : "flex" }}
                    >
                      <Car className="w-5 h-5 text-muted-foreground/40" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {listing.year} {listing.make} {listing.model}{listing.trim ? ` ${listing.trim}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatPrice(listing.askingPrice)} · {listing.mileage != null ? formatMileage(listing.mileage) : "—"} · {listing.daysListed === 0 ? t("marketplace:listedToday") : t("marketplace:listedDaysAgo", { count: listing.daysListed })}
                    </p>
                  </div>
                  <Badge className={`text-xs shrink-0 ${STATUS_COLORS[listing.status]}`}>
                    {listing.status === "pending" ? t("marketplace:status.pendingSale") : t(`marketplace:status.${listing.status}`)}
                  </Badge>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      data-testid={`button-edit-listing-${listing.id}`}
                      onClick={() => navigate(`/marketplace/${listing.id}/edit`)}
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    {listing.status === "active" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          data-testid={`button-mark-pending-${listing.id}`}
                          onClick={() => setStatusTarget({ listing, newStatus: "pending" })}
                        >
                          {t("marketplace:myListings.markPending")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-800"
                          data-testid={`button-mark-sold-active-${listing.id}`}
                          onClick={() => setSoldChannelTarget(listing)}
                        >
                          {t("marketplace:myListings.markSold")}
                        </Button>
                      </>
                    )}
                    {listing.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs"
                        data-testid={`button-mark-sold-${listing.id}`}
                        onClick={() => setSoldChannelTarget(listing)}
                      >
                        {t("marketplace:myListings.markSold")}
                      </Button>
                    )}
                    {listing.status === "draft" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-800"
                        data-testid={`button-publish-${listing.id}`}
                        onClick={() => setStatusTarget({ listing, newStatus: "active" })}
                      >
                        {t("marketplace:myListings.publish")}
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      data-testid={`button-delete-listing-${listing.id}`}
                      onClick={() => setDeleteTarget(listing)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          {collapsible && (
            <button
              className="w-full mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground py-1.5 transition-colors"
              data-testid={`button-toggle-group-${status}`}
              onClick={() => setExpandedGroups((prev) => ({ ...prev, [status]: !isExpanded }))}
            >
              {isExpanded ? (
                <><ChevronUp className="w-3.5 h-3.5" />{t("marketplace:myListings.showLess")}</>
              ) : (
                <><ChevronDown className="w-3.5 h-3.5" />{t("marketplace:myListings.showMore", { count: hiddenCount })}</>
              )}
            </button>
          )}
        </div>
        );
      })}

      {/* My Reputation */}
      {me?.id && (
        <div className="border-t border-border pt-6">
          <MyReputationSection
            userId={me.id}
          />
        </div>
      )}

      {/* Status update confirmation (non-sold transitions) */}
      <Dialog open={!!statusTarget} onOpenChange={() => setStatusTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("marketplace:statusChange.title")}</DialogTitle>
            <DialogDescription>
              {statusTarget && (
                <Trans
                  i18nKey="marketplace:statusChange.description"
                  values={{
                    vehicle: `${statusTarget.listing.year} ${statusTarget.listing.make} ${statusTarget.listing.model}`,
                    status: t(`marketplace:status.${statusTarget.newStatus}`),
                  }}
                  components={{ 1: <strong />, 3: <strong /> }}
                />
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusTarget(null)} data-testid="button-cancel-status-change">{t("marketplace:statusChange.cancel")}</Button>
            <Button
              data-testid="button-confirm-status-change"
              disabled={updateStatus.isPending}
              onClick={() => {
                if (!statusTarget) return;
                updateStatus.mutate({ id: statusTarget.listing.id, status: statusTarget.newStatus });
              }}
            >
              {updateStatus.isPending ? t("marketplace:statusChange.updating") : t("marketplace:statusChange.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sold channel dialog */}
      {soldChannelTarget && (
        <SoldChannelDialog
          listing={soldChannelTarget}
          open={!!soldChannelTarget}
          onClose={() => setSoldChannelTarget(null)}
          onConfirm={(soldChannel, buyerUserId) => {
            updateStatus.mutate({ id: soldChannelTarget.id, status: "sold", soldChannel, buyerUserId });
          }}
          isPending={updateStatus.isPending}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("marketplace:deleteListing.title")}</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <Trans
                  i18nKey="marketplace:deleteListing.description"
                  values={{ vehicle: `${deleteTarget.year} ${deleteTarget.make} ${deleteTarget.model}` }}
                  components={{ 1: <strong /> }}
                />
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="button-cancel-delete-listing">{t("marketplace:deleteListing.cancel")}</Button>
            <Button
              variant="destructive"
              data-testid="button-confirm-delete"
              disabled={deleteListing.isPending}
              onClick={() => deleteTarget && deleteListing.mutate(deleteTarget.id)}
            >
              {deleteListing.isPending ? t("marketplace:deleteListing.deleting") : t("marketplace:deleteListing.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default function MarketplacePage() {
  const { t } = useTranslation(["marketplace", "common"]);
  const [, navigate] = useLocation();

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/marketplace/unread-count"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/unread-count");
      return res.json();
    },
    refetchInterval: 30000,
  });
  const unreadCount = unreadData?.count ?? 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-violet-500" />
            {t("marketplace:page.title")}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {t("marketplace:page.subtitle")}
          </p>
        </div>
        <Button
          data-testid="button-list-vehicle"
          onClick={() => navigate("/marketplace/new")}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {t("marketplace:page.listVehicle")}
        </Button>
      </div>

      <Tabs defaultValue="browse">
        <div className="flex items-center gap-2">
          <TabsList className="flex-1">
            <TabsTrigger value="browse" data-testid="tab-browse">{t("marketplace:tabs.browse")}</TabsTrigger>
            <TabsTrigger value="my-listings" data-testid="tab-my-listings">{t("marketplace:tabs.myListings")}</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" onClick={() => navigate("/marketplace/messages")}>
              {t("marketplace:tabs.messages")}{unreadCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-violet-500 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 min-w-[18px]">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <Link href="/marketplace/settings">
            <button
              type="button"
              data-testid="button-marketplace-settings"
              title={t("marketplace:settingsButtonTitle")}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
          </Link>
        </div>

        <TabsContent value="browse" className="mt-4">
          <BrowseTab />
        </TabsContent>

        <TabsContent value="my-listings" className="mt-4">
          <MyListingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
